/**
 * Add-on enum labels, chip colours and money-field helpers (TMMPDM-0019), shared by the catalog
 * screen and the tenant Add-ons tab. English fallbacks for `addons.*`; colour is formatting only,
 * one field each.
 */
import type {
  AddonAllowedBilling,
  AddonBillingMode,
  AddonCatalogStatus,
  AddonChargeMode,
  AddonChargeStatus,
  AddonPeriod,
  CatalogPatchRequest,
  CatalogView,
  TenantAddonStatus,
} from '@/api/tmm/types';

/** 0009's rate-card currency list (`RateCardForm`), plus the item's own when it isn't on it. */
const CURRENCIES = ['USD', 'CRC', 'MXN', 'EUR', 'GBP', 'CAD'];

export function currencyOptions(current: string): string[] {
  return CURRENCIES.includes(current) ? CURRENCIES : [...CURRENCIES, current];
}

export const CATALOG_STATUS_FALLBACK: Record<AddonCatalogStatus, string> = {
  DRAFT: 'Draft',
  ACTIVE: 'Active',
  RETIRED: 'Retired',
};

export const PERIOD_FALLBACK: Record<AddonPeriod, string> = {
  ANNUAL: 'Annual',
  MONTHLY: 'Monthly',
};

/** "per year" / "per month" — the price-per-period suffix. */
export const PER_PERIOD_FALLBACK: Record<AddonPeriod, string> = {
  ANNUAL: '/ year',
  MONTHLY: '/ month',
};

export const ALLOWED_BILLING_FALLBACK: Record<AddonAllowedBilling, string> = {
  CARD: 'Card',
  NET_FROM_PAYOUTS: 'Net from payouts',
  BOTH: 'Card or net from payouts',
};

export const BILLING_MODE_FALLBACK: Record<AddonBillingMode, string> = {
  CARD: 'Card',
  NET_FROM_PAYOUTS: 'Net from payouts',
};

export const TENANT_ADDON_STATUSES: TenantAddonStatus[] = [
  'OFFERED',
  'ACTIVE',
  'PAST_DUE',
  'LAPSED',
  'CANCELLED',
  'DECLINED',
  'WITHDRAWN',
];

export const TENANT_ADDON_STATUS_FALLBACK: Record<TenantAddonStatus, string> = {
  OFFERED: 'Offered',
  ACTIVE: 'Active',
  PAST_DUE: 'Past due',
  LAPSED: 'Lapsed',
  CANCELLED: 'Cancelled',
  DECLINED: 'Declined',
  WITHDRAWN: 'Withdrawn',
};

export const CHARGE_STATUS_FALLBACK: Record<AddonChargeStatus, string> = {
  PENDING: 'Pending',
  PAID: 'Paid',
  FAILED: 'Failed',
  ACTION_REQUIRED: 'Action required',
  NETTING: 'Netting',
  NETTED: 'Netted',
  WAIVED: 'Waived',
  REFUNDED: 'Refunded',
};

export const CHARGE_MODE_FALLBACK: Record<AddonChargeMode, string> = {
  CARD: 'Card',
  NETTED: 'Netted',
};

/** A charge that is settled or already written off can't be waived. */
export function canWaiveCharge(status: AddonChargeStatus): boolean {
  return status !== 'PAID' && status !== 'NETTED' && status !== 'WAIVED' && status !== 'REFUNDED';
}

/** A non-blank money field that isn't a finite, non-negative number. */
export function invalidMoney(value: string): boolean {
  const n = Number(value.trim());
  return !Number.isFinite(n) || n < 0;
}

/** A catalogue edit's PATCH body: only what differs from the item as loaded. */
export function changedFields(
  item: CatalogView,
  next: {
    name: string;
    description: string;
    defaultPriceMinor: number | undefined;
    allowedBilling: AddonAllowedBilling;
  },
): CatalogPatchRequest {
  const body: CatalogPatchRequest = {};
  if (next.name !== item.name) body.name = next.name;
  if (next.description !== item.description) body.description = next.description;
  if (next.defaultPriceMinor !== undefined && next.defaultPriceMinor !== item.defaultPriceMinor) {
    body.defaultPriceMinor = next.defaultPriceMinor;
  }
  if (next.allowedBilling !== item.allowedBilling) body.allowedBilling = next.allowedBilling;
  return body;
}
